import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Platform, View } from "react-native";
import type { SwiperFlatListRefProps } from "react-native-swiper-flatlist/src/components/SwiperFlatList/SwiperFlatListProps";

interface UseImageSwiperControllerParams {
  images: string[];
  itemWidth: number;
  onIndexChange?: ({ index }: { index: number }) => any;
}

export const useImageSwiperController = ({
  images,
  itemWidth,
  onIndexChange,
}: UseImageSwiperControllerParams) => {
  const swiperRef = useRef<SwiperFlatListRefProps | null>(null);
  const wrapperRef = useRef<View | null>(null);
  const onIndexChangeRef = useRef(onIndexChange);
  const activeIndexRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const imagesSignature = images.join("|");

  const commitIndexChange = useCallback(
    (index: number) => {
      const nextIndex = Math.max(0, Math.min(images.length - 1, index));
      if (activeIndexRef.current === nextIndex) return;

      activeIndexRef.current = nextIndex;
      setActiveIndex(nextIndex);
      onIndexChangeRef.current?.({ index: nextIndex });
    },
    [images.length]
  );

  const syncIndexFromOffset = useCallback(
    (offsetX: number) => {
      const nextIndex = itemWidth > 0 ? Math.round(offsetX / itemWidth) : 0;
      commitIndexChange(nextIndex);
    },
    [commitIndexChange, itemWidth]
  );

  const goToIndex = useCallback(
    (idx: number, animated = true) => {
      if (!swiperRef.current) return;
      if (idx < 0 || idx >= images.length) return;

      commitIndexChange(idx);
      swiperRef.current.scrollToIndex({ index: idx, animated });
    },
    [commitIndexChange, images.length]
  );

  const goPrev = useCallback(() => {
    goToIndex(Math.max(0, activeIndex - 1), true);
  }, [activeIndex, goToIndex]);

  const goNext = useCallback(() => {
    goToIndex(Math.min(images.length - 1, activeIndex + 1), true);
  }, [activeIndex, goToIndex, images.length]);

  const focusWrapper = useCallback(() => {
    if (Platform.OS !== "web") return;

    const wrapperNode = wrapperRef.current as unknown as { focus?: () => void } | null;
    wrapperNode?.focus?.();
  }, []);

  useEffect(() => {
    onIndexChangeRef.current = onIndexChange;
  }, [onIndexChange]);

  useEffect(() => {
    activeIndexRef.current = 0;
    setActiveIndex(0);
    onIndexChangeRef.current?.({ index: 0 });

    requestAnimationFrame(() => {
      swiperRef.current?.goToFirstIndex();
    });
  }, [imagesSignature]);

  useLayoutEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const activeElement = document.activeElement as HTMLElement | null;
      const swiperNode = swiperRef.current as unknown as { el?: HTMLElement } | null;
      const isSwiperFocused =
        Boolean(swiperNode?.el && activeElement && swiperNode.el.contains(activeElement)) ||
        activeElement?.dataset?.imageSwiperFocusable === "true";

      if (!isSwiperFocused) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goPrev();
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        goNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [goNext, goPrev]);

  const webFocusableProps =
    Platform.OS === "web"
      ? ({
          tabIndex: 0,
          "data-image-swiper-focusable": "true",
        } as Record<string, unknown>)
      : {};

  return {
    activeIndex,
    commitIndexChange,
    focusWrapper,
    goNext,
    goPrev,
    goToIndex,
    swiperRef,
    syncIndexFromOffset,
    webFocusableProps,
    wrapperRef,
  };
};
